import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { toast } from 'sonner';
import { authAPI, getErrorMessage } from '../../utils/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Checkbox } from '../../components/ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { Eye, EyeOff, ArrowRight } from 'lucide-react';
import { countries } from '../../utils/constants';

const Register = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    first_name: '',
    last_name: '',
    country: '',
  });
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [acceptedTerms, setAcceptedTerms] = useState(false);

  // Create phone code mapping from countries array
  const countryPhoneCodes = {};
  countries.forEach((country) => {
    countryPhoneCodes[country.name] = country.dialCode;
  });

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!acceptedTerms) {
      toast.error('Please accept the terms and conditions');
      return;
    }

    if (formData.password !== formData.confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }

    if (formData.password.length < 8) {
      toast.error('Password must be at least 8 characters');
      return;
    }

    setLoading(true);

    try {
      const phoneNumber =
        formData.phoneCode && formData.phone
          ? `${formData.phoneCode} ${formData.phone}`
          : formData.phone;

      await authAPI.register({
        email: formData.email,
        password: formData.password,
        first_name: formData.first_name,
        last_name: formData.last_name,
        phone: phoneNumber,
      });

      toast.success('Registration successful! Please check your email for the verification link.');
      // Store email for verification page
      localStorage.setItem('pendingEmailVerification', formData.email);
      navigate('/auth/email-verification');
    } catch (error) {
      toast.error(getErrorMessage(error, 'Registration failed'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center h-full bg-[#060638]">
      <div className="w-full overflow-hidden flex flex-col md:flex-row min-h-[80vh] md:h-[90vh] my-auto gap-6 md:gap-10 bg-transparent px-4 md:px-6 pt-4 md:pt-0">
        {/* Left Panel - Image */}
        <div className="w-full md:w-1/2 flex items-center justify-center relative overflow-hidden rounded-2xl min-h-[240px] sm:min-h-[320px] md:min-h-0 md:h-full shrink-0 p-4 md:p-0">
          <img
            src="/signup.png"
            alt=""
            className="w-full h-auto object-contain rounded-2xl md:h-full md:w-full"
          />
        </div>

        {/* Right Panel - Form */}
        <div className="w-full md:w-1/2 flex flex-col justify-start p-4 md:p-8 lg:p-10 xl:p-12 overflow-auto">
          <div className="flex items-center justify-between w-full max-w-[630px] mb-6 md:mb-10 lg:mb-12">
            <img src="/images/logo.png" alt="Logo" className="h-8 md:h-10 lg:h-12 mr-auto" />
            <Link
              to="/login"
              className="text-white hover:underline items-center font-medium border border-white rounded-full px-3 md:px-4 lg:px-5 py-1.5 md:py-2 justify-center text-sm md:text-base"
              data-testid="register-link"
            >
              Login
            </Link>
          </div>
          <h1 className="text-2xl md:text-3xl lg:text-[44px] font-bold text-white mb-6 md:mb-8 lg:mb-10">
            <span className="!text-white">Get started</span>
          </h1>

          <form
            onSubmit={handleSubmit}
            className="flex flex-col gap-4 w-full max-w-[630px]"
            data-testid="register-form"
          >
            <div>
              {/* <Label
                htmlFor="country"
                className="text-sm font-medium text-white mb-2 block"
              >
                Country <span className="text-red-500">*</span>
              </Label> */}
              <Select
                value={formData.country}
                onValueChange={(value) =>
                  setFormData({
                    ...formData,
                    country: value,
                    phoneCode: countryPhoneCodes[value] || '+1',
                  })
                }
              >
                <SelectTrigger className="w-full h-[50px] !bg-white border-0 text-[16px] rounded-none px-4 !text-gray-900 placeholder:text-gray-400 placeholder:text-[16px] focus:ring-2 focus:ring-green-500 shadow-sm">
                  <SelectValue placeholder="Select country" />
                </SelectTrigger>
                <SelectContent>
                  {countries.map((country) => (
                    <SelectItem key={country.iso2} value={country.name}>
                      {country.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="flex flex-col md:flex-row gap-4 md:gap-6 w-full">
              <div className="w-full md:w-1/2">
                {/* <Label
                htmlFor="first_name"
                className="text-sm font-medium text-white mb-2 block"
              >
                First Name <span className="text-red-500">*</span>
              </Label> */}
                <Input
                  id="first_name"
                  type="text"
                  placeholder="Legal first name"
                  value={formData.first_name}
                  onChange={(e) =>
                    setFormData({ ...formData, first_name: e.target.value })
                  }
                  className="w-full h-[50px] !bg-white border-0 text-[16px] rounded-none px-4 !text-gray-900 placeholder:text-gray-400 placeholder:text-[16px] focus:ring-2 focus:ring-green-500 focus:outline-none shadow-sm"
                  required
                  data-testid="firstname-input"
                />
              </div>
              <div className="w-full md:w-1/2">
                {/* <Label
                htmlFor="last_name"
                className="text-sm font-medium text-white mb-2 block"
              >
                First Name <span className="text-red-500">*</span>
              </Label> */}
                <Input
                  id="last_name"
                  type="text"
                  placeholder="Legal last name"
                  value={formData.last_name}
                  onChange={(e) =>
                    setFormData({ ...formData, last_name: e.target.value })
                  }
                  className="w-full h-[50px] !bg-white border-0 text-[16px] rounded-none px-4 !text-gray-900 placeholder:text-gray-400 placeholder:text-[16px] focus:ring-2 focus:ring-green-500 focus:outline-none shadow-sm"
                  required
                  data-testid="lastname-input"
                />
              </div>
            </div>

            <div>
              {/* <Label
                htmlFor="email"
                className="text-sm font-medium text-white mb-2 block"
              >
                Email <span className="text-red-500">*</span>
              </Label> */}
              <Input
                id="email"
                type="email"
                placeholder="Email address"
                value={formData.email}
                onChange={(e) =>
                  setFormData({ ...formData, email: e.target.value })
                }
                className="w-full h-12 !bg-white border-0 text-[16px] rounded-none px-4 !text-gray-900 placeholder:text-gray-400 placeholder:text-[16px] focus:ring-2 focus:ring-green-500 focus:outline-none shadow-sm"
                required
                data-testid="email-input"
              />
            </div>

            <div>
              {/* <Label
                htmlFor="password"
                className="text-sm font-medium text-white mb-2 block"
              >
                Password <span className="text-red-500">*</span>
              </Label> */}
              <div className="relative">
                <Input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  placeholder="Password"
                  value={formData.password}
                  onChange={(e) =>
                    setFormData({ ...formData, password: e.target.value })
                  }
                  className="w-full h-12 !bg-white border-0 text-[16px] rounded-none px-4 pr-12 !text-gray-900 placeholder:text-gray-400 placeholder:text-[16px] focus:ring-2 focus:ring-green-500 focus:outline-none shadow-sm"
                  required
                  data-testid="password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  {showPassword ? (
                    <EyeOff className="w-5 h-5" />
                  ) : (
                    <Eye className="w-5 h-5" />
                  )}
                </button>
              </div>
            </div>

            {/* Confirm Password Field */}
            <div>
              {/* <Label
                htmlFor="confirmPassword"
                className="text-sm font-medium text-white mb-2 block"
              >
                Confirm Password <span className="text-red-500">*</span>
              </Label> */}
              <div className="relative">
                <Input
                  id="confirmPassword"
                  type={showConfirmPassword ? 'text' : 'password'}
                  placeholder="Confirm password"
                  value={formData.confirmPassword}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      confirmPassword: e.target.value,
                    })
                  }
                  className="w-full h-12 !bg-white border-0 text-[16px] rounded-none px-4 pr-12 !text-gray-900 placeholder:text-gray-400 placeholder:text-[16px] focus:ring-2 focus:ring-green-500 focus:outline-none shadow-sm"
                  required
                  data-testid="confirm-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  {showConfirmPassword ? (
                    <EyeOff className="w-5 h-5" />
                  ) : (
                    <Eye className="w-5 h-5" />
                  )}
                </button>
              </div>
            </div>
            {/* End Confirm Password Field */}

            <div className="flex items-start gap-3">
              <Checkbox
                id="terms"
                checked={acceptedTerms}
                onCheckedChange={setAcceptedTerms}
                className="mt-1"
              />
              <Label
                htmlFor="terms"
                className="text-sm text-white leading-relaxed"
              >
                By creating an account, I agree that I am at least 18 years old and agree to AquaBlockChain&apos;s Terms of Business, and aknowledge their privacy policy.
              </Label>
            </div>

            <Button
              type="submit"
              className="w-full h-14 bg-[#ff2d2d] hover:bg-[#7f1d1d] text-white font-semibold rounded-none shadow-xl flex items-center justify-center gap-2 transition-all duration-300 transition-ease-in-out mt-2"
              disabled={loading}
              data-testid="register-button"
            >
              {loading ? (
                <div className="loading-spinner"></div>
              ) : (
                <>
                  <span>Create Account</span>
                </>
              )}
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default Register;
